import { useState } from 'react';
import { createPortal } from 'react-dom';
import { apiJson } from '../lib/api.js';

const backdrop = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(8, 4, 22, 0.78)',
  backdropFilter: 'blur(6px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  padding: 16,
};
const card = {
  background: '#fff',
  borderRadius: 16,
  padding: '28px 30px',
  width: '100%',
  maxWidth: 560,
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: '0 30px 80px rgba(0, 0, 0, 0.3)',
};
const tabBar = {
  display: 'flex',
  gap: 4,
  borderBottom: '1px solid #ece8f6',
  marginBottom: 18,
  marginTop: 18,
};
const tabBtn = (active) => ({
  padding: '10px 14px',
  background: 'transparent',
  border: 'none',
  borderBottom: active ? '2px solid #5b21b6' : '2px solid transparent',
  color: active ? '#5b21b6' : '#6b7280',
  fontWeight: active ? 700 : 500,
  fontSize: 13.5,
  cursor: 'pointer',
});
const label = { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#3f3a5e' };
const input = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 10,
  border: '1px solid #d8d6e6',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};
const primaryBtn = {
  padding: '10px 16px',
  borderRadius: 10,
  border: 'none',
  background: 'linear-gradient(135deg, #5b21b6, #7c3aed)',
  color: '#fff',
  fontWeight: 700,
  cursor: 'pointer',
  fontSize: 14,
};
const secondaryBtn = {
  padding: '10px 14px',
  borderRadius: 10,
  border: '1px solid #d8d6e6',
  background: '#fff',
  color: '#3f3a5e',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 13.5,
};
const successBox = { background: '#dcfce7', color: '#166534', padding: 10, borderRadius: 8, fontSize: 13, marginTop: 12 };
const errorBox = { background: '#fee2e2', color: '#991b1b', padding: 10, borderRadius: 8, fontSize: 13, marginTop: 12 };

export default function UserActionsModal({ targetUser, onClose, onDidUpdate }) {
  const [tab, setTab] = useState('email');
  const [status, setStatus] = useState({ kind: 'idle', message: '' });

  if (!targetUser) return null;

  return createPortal(
    <div style={backdrop} onClick={onClose}>
      <div className="mm-auth-card" style={card} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#5b21b6', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
              Manage user
            </div>
            <h2 style={{ margin: '4px 0 2px', fontSize: 20 }}>{targetUser.name || '—'}</h2>
            <div style={{ color: '#6b7280', fontSize: 13 }}>
              {targetUser.email} · <span style={{ textTransform: 'capitalize' }}>{targetUser.role}</span>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'transparent', border: 'none', color: '#6b7280', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>

        <div style={tabBar}>
          <button style={tabBtn(tab === 'email')} onClick={() => setTab('email')}>Send email</button>
          <button style={tabBtn(tab === 'reset-link')} onClick={() => setTab('reset-link')}>Reset link</button>
          <button style={tabBtn(tab === 'set-password')} onClick={() => setTab('set-password')}>Set password</button>
          <button style={tabBtn(tab === 'mailchimp')} onClick={() => setTab('mailchimp')}>Mailchimp</button>
        </div>

        {tab === 'email' && (
          <SendEmail userId={targetUser.id} setStatus={setStatus} />
        )}
        {tab === 'reset-link' && (
          <SendResetLink userId={targetUser.id} setStatus={setStatus} />
        )}
        {tab === 'set-password' && (
          <SetPassword userId={targetUser.id} setStatus={setStatus} onDone={onDidUpdate} />
        )}
        {tab === 'mailchimp' && (
          <MailchimpSync userId={targetUser.id} setStatus={setStatus} />
        )}

        {status.kind === 'success' && <div style={successBox}>{status.message}</div>}
        {status.kind === 'error' && <div className="mm-auth-error" style={errorBox}>{status.message}</div>}
      </div>
    </div>,
    document.body,
  );
}

function SendEmail({ userId, setStatus }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ kind: 'idle' });
    setSubmitting(true);
    try {
      const res = await apiJson(`/api/users/${userId}/send-email`, {
        method: 'POST',
        body: { subject, body },
      });
      setStatus({
        kind: 'success',
        message: res.stubbed
          ? 'Email logged to server console (GMAIL_USER / Google OAuth not configured yet).'
          : 'Email sent ✓',
      });
      setSubject('');
      setBody('');
    } catch (e2) {
      setStatus({ kind: 'error', message: e2.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <p style={{ fontSize: 13.5, color: '#6b7280', margin: '0 0 14px' }}>
        Send a one-off email from your connected Medic Mind Gmail account.
      </p>
      <div style={{ marginBottom: 14 }}>
        <label style={label}>Subject</label>
        <input
          style={input}
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          required
          placeholder="A short, clear subject"
        />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={label}>Message</label>
        <textarea
          style={{ ...input, minHeight: 160, resize: 'vertical' }}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          required
          placeholder={`Hi ${'{first name will be added}'},\n\nWrite your message here…`}
        />
      </div>
      <button type="submit" style={primaryBtn} disabled={submitting || !subject || !body}>
        {submitting ? 'Sending…' : 'Send email'}
      </button>
    </form>
  );
}

function SendResetLink({ userId, setStatus }) {
  const [submitting, setSubmitting] = useState(false);

  const send = async () => {
    setStatus({ kind: 'idle' });
    setSubmitting(true);
    try {
      const res = await apiJson(`/api/users/${userId}/send-password-reset`, { method: 'POST' });
      setStatus({
        kind: 'success',
        message: res.stubbed
          ? 'Reset link generated and logged to server console (no email configured).'
          : 'Password reset email sent ✓',
      });
    } catch (e2) {
      setStatus({ kind: 'error', message: e2.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <p style={{ fontSize: 13.5, color: '#3f3a5e', margin: '0 0 16px' }}>
        Generates a one-time password-reset link and emails it to the user. The link
        expires in 60 minutes and all their existing sessions stay valid until they
        actually reset.
      </p>
      <button type="button" style={primaryBtn} onClick={send} disabled={submitting}>
        {submitting ? 'Sending…' : 'Email password reset link'}
      </button>
    </div>
  );
}

function SetPassword({ userId, setStatus, onDone }) {
  const [newPassword, setNewPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ kind: 'idle' });
    setSubmitting(true);
    try {
      await apiJson(`/api/users/${userId}/reset-password`, {
        method: 'POST',
        body: { newPassword },
      });
      setStatus({ kind: 'success', message: 'Password updated. All their active sessions have been revoked.' });
      setNewPassword('');
      onDone?.();
    } catch (e2) {
      setStatus({ kind: 'error', message: e2.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <p style={{ fontSize: 13.5, color: '#3f3a5e', margin: '0 0 14px' }}>
        Set a new password directly. The user will be signed out of any active sessions
        immediately. Share the new password with them through a safe channel.
      </p>
      <label style={label}>New password (8+ chars, at least one letter and one number)</label>
      <input
        style={input}
        type="text"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        required
        minLength={8}
        placeholder="e.g. TempPass2026!"
      />
      <button type="submit" style={{ ...primaryBtn, marginTop: 14 }} disabled={submitting || newPassword.length < 8}>
        {submitting ? 'Saving…' : 'Set new password'}
      </button>
    </form>
  );
}

function MailchimpSync({ userId, setStatus }) {
  const [submitting, setSubmitting] = useState(false);

  const send = async () => {
    setStatus({ kind: 'idle' });
    setSubmitting(true);
    try {
      const res = await apiJson(`/api/users/${userId}/mailchimp-sync`, { method: 'POST' });
      if (res.skipped) {
        setStatus({ kind: 'error', message: 'Mailchimp not configured (MAILCHIMP_API_KEY / MAILCHIMP_AUDIENCE_ID missing).' });
      } else if (res.ok) {
        setStatus({ kind: 'success', message: 'User pushed to Mailchimp ✓' });
      } else {
        setStatus({ kind: 'error', message: res.error || 'Mailchimp call failed.' });
      }
    } catch (e2) {
      setStatus({ kind: 'error', message: e2.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <p style={{ fontSize: 13.5, color: '#3f3a5e', margin: '0 0 16px' }}>
        Push this user to your Mailchimp audience as <code>subscribed</code> (no double
        opt-in). Use this for users who joined before the marketing-consent flow or to
        re-sync if the audience drifted.
      </p>
      <button type="button" style={primaryBtn} onClick={send} disabled={submitting}>
        {submitting ? 'Syncing…' : 'Sync to Mailchimp'}
      </button>
    </div>
  );
}
